import { EntityType, EntityTypeEnum, OrderUpdate, ServerActionTypes } from 'api'
import { RxWebSocketReadyState } from 'messaging'
import { ConnectionActionTypes } from 'store/connection'
import { Repository, RepositoryEntry, RepositoryReducerActions } from './repositoryTypes'
import { RepositoryActionTypes } from './repositoryActions'
import { EntityReducer, entityStoreInitialState } from './entityReducer'

export const initialState: Repository = {
  [EntityType.APPLICATION_ALERT]: entityStoreInitialState,
  [EntityType.AXE]: entityStoreInitialState,
  [EntityType.AUDIT_ENTRY]: entityStoreInitialState,
  [EntityType.AUTHORISED_USER]: entityStoreInitialState,
  [EntityType.BEST_PRICE]: entityStoreInitialState,
  [EntityType.BATCH_DATA]: entityStoreInitialState,
  [EntityType.CLUSTER_PONG]: entityStoreInitialState,
  [EntityType.CLEARING_HOUSE]: entityStoreInitialState,
  [EntityType.CLEARING_HOUSE_INTERACTION]: entityStoreInitialState,
  [EntityType.COLLATERAL]: entityStoreInitialState,
  [EntityType.COLLATERAL_DETAILS]: entityStoreInitialState,
  [EntityType.COLLATERAL_PRICE]: entityStoreInitialState,
  [EntityType.COLLATERAL_BASKET]: entityStoreInitialState,
  [EntityType.COLLATERAL_UPLOAD]: entityStoreInitialState,
  [EntityType.COLLATERAL_REFINITIV_IMPORT_RESULT]: entityStoreInitialState,
  [EntityType.CLUSTER_CLIENT_STATE]: entityStoreInitialState,
  [EntityType.CLUSTER_SESSION]: entityStoreInitialState,
  [EntityType.COLLECTION]: entityStoreInitialState,
  [EntityType.COMPLIANCE_COLLATERAL]: entityStoreInitialState,
  [EntityType.COUNTER]: entityStoreInitialState,
  [EntityType.COUNTRY]: entityStoreInitialState,
  [EntityType.DATABASE_REPORT_REQUEST]: entityStoreInitialState,
  [EntityType.DEAL]: entityStoreInitialState,
  [EntityType.DEAL_HISTORY]: entityStoreInitialState,
  [EntityType.DEAL_INTEREST_PERIOD]: entityStoreInitialState,
  [EntityType.DEAL_EXPORT]: entityStoreInitialState,
  [EntityType.DEV_SIGNAL]: entityStoreInitialState,
  [EntityType.EGRESS_POSITION]: entityStoreInitialState,
  [EntityType.EONIA_RATE]: entityStoreInitialState,
  [EntityType.ESTR_RATE]: entityStoreInitialState,
  [EntityType.FAT_FINGER_PRODUCT_SECTOR]: entityStoreInitialState,
  [EntityType.FAT_FINGER_USER]: entityStoreInitialState,
  [EntityType.FX_RATE]: entityStoreInitialState,
  [EntityType.HAIRCUT_BAND]: entityStoreInitialState,
  [EntityType.HAIRCUT_COLLATERAL_BAND]: entityStoreInitialState,
  [EntityType.HAIRCUT_ENTRY]: entityStoreInitialState,
  [EntityType.HOLIDAY]: entityStoreInitialState,
  [EntityType.INDEX]: entityStoreInitialState,
  [EntityType.IN_FLIGHT_ORGANISATION]: entityStoreInitialState,
  [EntityType.IN_FLIGHT_TARGET_ORGANISATION]: entityStoreInitialState,
  [EntityType.IMPORT_COMPLETE]: entityStoreInitialState,
  [EntityType.NEGOTIATION_AGGREGATE]: entityStoreInitialState,
  [EntityType.NEGOTIATION]: entityStoreInitialState,
  [EntityType.NEGOTIATION_OVERRIDE]: entityStoreInitialState,
  [EntityType.NEGOTIATION_HISTORY]: entityStoreInitialState,
  [EntityType.NOTES]: entityStoreInitialState,
  [EntityType.ORDER]: entityStoreInitialState,
  [EntityType.ORDER_RECORD_KEEPING]: entityStoreInitialState,
  [EntityType.ORGANISATION]: entityStoreInitialState,
  [EntityType.ISSUER_ORGANISATION]: entityStoreInitialState,
  [EntityType.ORGANISATION_META]: entityStoreInitialState,
  [EntityType.PERMISSION_REPORT_REQUEST]: entityStoreInitialState,
  [EntityType.PLATFORM]: entityStoreInitialState,
  [EntityType.PRIME_BROKER]: entityStoreInitialState,
  [EntityType.PRODUCT]: entityStoreInitialState,
  [EntityType.PRODUCT_SECTOR]: entityStoreInitialState,
  [EntityType.PRODUCT_SECTOR_MAPPING]: entityStoreInitialState,
  [EntityType.PRODUCT_SECTOR_MAPPING_DRAFT]: entityStoreInitialState,
  [EntityType.PRODUCT_SECTOR_MAPPING_STATE]: entityStoreInitialState,
  [EntityType.QUERY_REQUEST]: entityStoreInitialState,
  [EntityType.REFERENCE_DATA_MANAGEMENT_DATA]: entityStoreInitialState,
  [EntityType.REFERENCE_RATE]: entityStoreInitialState,
  [EntityType.RETRY_FAILED_EXPORT]: entityStoreInitialState,
  [EntityType.RESET_SOTW]: entityStoreInitialState,
  [EntityType.SOTW_SENT]: entityStoreInitialState,
  [EntityType.SPONSOR]: entityStoreInitialState,
  [EntityType.SPREADSHEET_MAPPING]: entityStoreInitialState,
  [EntityType.TRADING_CONTROL_LIMIT]: entityStoreInitialState,
  [EntityType.TRADING_CONTROL_LIMIT_USER_UTILISATION]: entityStoreInitialState,
  [EntityType.TRADING_ENTITY]: entityStoreInitialState,
  [EntityType.TRADING_ENTITY_CLEARING]: entityStoreInitialState,
  [EntityType.TRADING_ENTITY_META]: entityStoreInitialState,
  [EntityType.TRADING_RELATIONSHIP]: entityStoreInitialState,
  [EntityType.USER]: entityStoreInitialState,
  [EntityType.USER_PREFERENCE]: entityStoreInitialState,
  [EntityType.USER_SETTINGS]: entityStoreInitialState,
  [EntityType.WORKING_DAYS]: entityStoreInitialState
}

const entityReducer = new EntityReducer()

export function repositoryReducer(state: Repository = initialState, action: RepositoryReducerActions): Repository {
  switch (action.type) {
    case ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_BEGIN:
    case ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END: {
      entityReducer.resetSnapshotInternalState()
      return state
    }
    case ServerActionTypes.ENTITY_SNAPSHOT_BEGIN:
    case ServerActionTypes.ENTITY_SNAPSHOT_END:
    case RepositoryActionTypes.REPOSITORY_REVERT_UPDATES:
    case RepositoryActionTypes.REPOSITORY_UPDATE_FIELD:
    case RepositoryActionTypes.REPOSITORY_UPDATE_META:
    case ServerActionTypes.ENTITY_DELETE: {
      const entityType = action.payload.entityType
      if (entityType === EntityType.NEGOTIATION) {
        return state
      }
      // TODO needs proper implementation
      if (entityType === EntityTypeEnum.TRANSACTION_META) {
        return state
      }

      if (entityType === EntityTypeEnum.RESPONSE_CLUSTER_MESSAGE) {
        return state
      }

      if (entityType === EntityTypeEnum.EGRESS_SOTW_PUBLISH) {
        return state
      }

      if (entityType === EntityTypeEnum.COMMAND) {
        return state
      }

      if (entityType === EntityTypeEnum.RESET_SOTW) {
        return state
      }

      if (entityType === EntityTypeEnum.SCHEDULED_TASK_STATE) {
        return state
      }

      if (entityType === EntityTypeEnum.SOTW_START_FROM_CHECKPOINT) {
        return state
      }

      const entityStore = state[entityType]
      const updatedStore = entityReducer.run(entityStore, action, '', entityType === EntityType.NEGOTIATION_HISTORY)
      return {
        ...state,
        [entityType]: updatedStore
      }
    }

    case ServerActionTypes.ENTITY_UPDATE: {
      const entityType = action.payload.update.entityType
      if (entityType === EntityType.NEGOTIATION) {
        return state
      }

      const entityStore = state[entityType]
      const updatedStore = entityReducer.run(entityStore, action, '', entityType === EntityType.NEGOTIATION_HISTORY)
      return {
        ...state,
        [entityType]: updatedStore
      }
    }

    case ServerActionTypes.ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS: {
      const { entityType } = action.payload

      // TODO needs proper implementation
      if (entityType === EntityTypeEnum.TRANSACTION_META) {
        return state
      }
      if (entityType === EntityTypeEnum.RESPONSE_CLUSTER_MESSAGE) {
        return state
      }
      if (entityType === EntityTypeEnum.EGRESS_SOTW_PUBLISH) {
        return state
      }
      if (entityType === EntityTypeEnum.SCHEDULED_TASK_STATE) {
        return state
      }
      if (entityType === EntityTypeEnum.COMMAND) {
        return state
      }
      if (entityType === EntityTypeEnum.RESET_SOTW) {
        return state
      }
      if (entityType === EntityTypeEnum.SOTW_START_FROM_CHECKPOINT) {
        return state
      }

      const entityStore = state[entityType]

      const updatedStore = entityReducer.run(entityStore, action)

      return {
        ...state,
        [entityType]: updatedStore
      }
    }

    case ServerActionTypes.EVENT_RESPONSE: {
      const entityStore = state[EntityTypeEnum.ORDER]

      const updatedStore = entityReducer.run(entityStore, action) as Record<string, RepositoryEntry<OrderUpdate>>

      if (updatedStore === entityStore) {
        return state
      }

      return {
        ...state,
        [EntityTypeEnum.ORDER]: updatedStore
      }
    }

    case ConnectionActionTypes.SOCKET_STATUS:
      return action.payload.readyState === RxWebSocketReadyState.Closed ? initialState : state

    case RepositoryActionTypes.REPOSITORY_RESET: {
      return initialState
    }

    default: {
      return state
    }
  }
}
